import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FormControl, InputLabel, LinearProgress, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { match, P } from 'ts-pattern';
import * as R from 'remeda';

import { oshaHipaaIcon } from '../../../icons';

import './OshaHipaa.css';

import {
  selectPrograms,
  getData,
  selectLoadingState,
  selectSnackbarMessage,
  setCurrentYear,
  selectCustomer,
  clearProgramData,
} from './slice';
import ProgramDirections from './ProgramDirections';
import { oldDeCutoffYear } from './constants';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import constants from '../../../constants';
import PageHeader from '../../../components/PageHeader';
import { addError, addSuccess } from '../../../redux/alerts';
import useCustomerNumber from '../../../hooks/useCustomerNumber';
import useHasDE from './hooks/useHasDE';
import useIsCorpView from '../../../hooks/useIsCorpView';

export default function PracticeOshaHipaa() {
  const isChildCustomer = useIsCorpView()
  const customer = useAppSelector(selectCustomer)
  const snackbarMessage = useAppSelector(selectSnackbarMessage);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (snackbarMessage) {
      if (snackbarMessage.type === 'normal') {
        dispatch(addSuccess(snackbarMessage.message));
      } else {
        dispatch(addError(snackbarMessage.message));
      }
    }
  }, [snackbarMessage]);

  const navigate = useNavigate();

  let { year } = useParams();

  const hasDE = useHasDE()

  const customerNumber = useCustomerNumber()

  const loadingState = useSelector(selectLoadingState);
  const isLoading = loadingState === 'loading' || loadingState === 'not-asked';
  const hasError = loadingState === 'error';

  const setProgramYear = (year?: string): void => {
    if (!year && hasDE) {
      // Program year wasn't in list, but they have de so use old cut off year as the value
      return setProgramYear(oldDeCutoffYear);
    } else if (!year) {
      // Nothing was provided and they don't have de, so do nothing
      return;
    }

    if (isChildCustomer) {
      navigate(`/corp/practice/${customerNumber}/osha-hipaa/${year}`);
    } else {
      navigate(`/practice/osha-hipaa/${year}`);
    }
  };

  const handleSelectProgramYear = (e: SelectChangeEvent) => {
    setProgramYear(e.target.value);
  };

  const programs = useSelector(selectPrograms);

  const programYears = useMemo(() => {
    return [...new Set(programs?.map((program) => String(program.year)))].sort().reverse();
  }, [programs]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (programYears && programYears.length > 0) {
      const latestYear = programYears[0];
      
      // Allow oldDeCutoffYear if user has DE, even if it's not in programYears
      const isValidYear = year && (programYears.includes(year) || (year === oldDeCutoffYear && hasDE));
      
      if (!year || !isValidYear) {
        setProgramYear(latestYear);
      } else {
        dispatch(setCurrentYear(Number(year)));
      }
    } else {
      if (year) {
        dispatch(setCurrentYear(Number(year)));
      } else if (hasDE) {
        setProgramYear(oldDeCutoffYear);
      }
    }
  }, [year, programYears, hasDE, isLoading]);

  useEffect(() => {
    const needsData = Boolean(customerNumber) && !customer && loadingState === 'not-asked'
    const clearData = customer && customerNumber !== customer.customer_number

    if (needsData) {
      dispatch(getData(customerNumber))
    } else if (clearData) {
      dispatch(clearProgramData())
    }
  }, [customerNumber, customer, loadingState])

  const errorSnippet = <p>An error has occurred, please try again.</p>;

  return (
    <>
      <PageHeader src={oshaHipaaIcon} headerName={'OSHA/HIPAA'} />
      <ErrorBoundary fallback={errorSnippet}>
        {match({ hasError, isLoading, hasDE, programs, year })
          .with({ hasError: true }, () => errorSnippet)
          .with({ isLoading: true }, () => <LinearProgress data-testid="osha-hipaa-loading-bar" />)
          .with({ isLoading: false, programs: P.when(R.isEmpty), hasDE: false }, () => (
            <iframe className="iframe-sales-page" data-testid="sales-iframe" src={constants.OSHA_HIPAA_TRAINING_SALES_PAGE_IFRAME_URL} />
          ))
          .with(
            P.intersection({ year: P.select(P.not(P.nullish)), isLoading: false }, P.union({ programs: P.not(P.when(R.isEmpty)) }, { hasDE: true })),
            (programYear) => (
              <div className="m-16">
                <div className="flex flex-end">
                  <div className="flex flex-column">
                    <FormControl sx={{ minWidth: 150 }} size="small">
                      <InputLabel>Program Year</InputLabel>
                      {/** For the matter of speed, i'm just putting a testid here, but ideally we should use MUI in a way were we can select this select by its label, which isn't possible with this current markup */}
                      <Select autoWidth value={programYear} label="Program Year" onChange={handleSelectProgramYear} data-testid="program-year-select">
                        {programYears.map((y) =>
                          y != oldDeCutoffYear ? ( // Don't double include de cut-off year
                            <MenuItem value={y} key={y}>
                              {y}
                            </MenuItem>
                          ) : null
                        )}
                        {hasDE && <MenuItem value={oldDeCutoffYear}>{oldDeCutoffYear} and Prior</MenuItem>}
                      </Select>
                    </FormControl>
                    <ProgramDirections />
                  </div>
                </div>
                <Outlet />
              </div>
            )
          )
          .otherwise(() => errorSnippet)}
      </ErrorBoundary>
    </>
  );
}
